import axios from 'axios';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/api/v1`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request interceptor: attach token ────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor: auto-refresh on 401 ────────────────────────────────
let isRefreshing = false;
let failedQueue  = [];   // Requests queued while a refresh is in-flight

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    // ── No response (network error) ──────────────────────────────────────────
    if (!error.response) return Promise.reject(error);

    const { status } = error.response;

    // ── 403 Forbidden: wrong role — do NOT log out, just reject ──────────────
    // This is an authorization error (correct user, wrong permissions).
    // Logging out here was the cause of the "intermittent unauthorized" bug.
    if (status === 403) return Promise.reject(error);

    // ── Don't retry refresh endpoint or already-retried requests ─────────────
    if (original._retry || original.url?.includes('/auth/refresh-token')) {
      return Promise.reject(error);
    }

    // ── 401 Unauthorized: token expired — try to refresh ─────────────────────
    if (status === 401) {
      // Don't do this for the login endpoint itself, so we can display invalid credential errors
      if (original.url?.includes('/auth/login')) {
        return Promise.reject(error);
      }
      if (isRefreshing) {
        // Queue this request until the refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          original.headers.Authorization = `Bearer ${token}`;
          return api(original);
        });
      }

      original._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        // No refresh token — session is truly gone
        isRefreshing = false;
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(
  `${process.env.REACT_APP_API_URL}/api/v1/auth/refresh-token`,
  { refreshToken }
);
        const { accessToken: newAccess, refreshToken: newRefresh } = data.data;

        // Store new tokens
        localStorage.setItem('accessToken', newAccess);
        localStorage.setItem('refreshToken', newRefresh);

        // Update default header for future requests
        api.defaults.headers.common['Authorization'] = `Bearer ${newAccess}`;

        // Retry all queued requests with the new token
        processQueue(null, newAccess);

        // Retry the original failed request
        original.headers.Authorization = `Bearer ${newAccess}`;
        return api(original);
      } catch (refreshError) {
        // Refresh also failed → force logout
        processQueue(refreshError, null);
        localStorage.clear();
        delete api.defaults.headers.common['Authorization'];
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
